import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import { RootState } from '../../../core/store/store';
import { logout } from '../../../core/store/slices/userSlice';
import SessionService from '../../../shared/services/sessionService';
import useUserProfileSync from '../../../shared/hooks/useUserProfileSync';
import { useKeyboardShortcuts } from '../../../shared/hooks/useKeyboardShortcuts';
import BackgroundImage from '../../../shared/components/BackgroundImage';
import StatsSection from './components/StatsSection';
import SupportModal from './components/SupportModal';
import SettingsModal from './components/SettingsModal';
import RankingModal from './components/RankingModal';
import ShopModal from './components/ShopModal';
import usePageTranslation from '../../../shared/hooks/usePageTranslation';
import { roleLabelFromStatsT } from './roleLabel';
import { isPanelTeamMember } from '../../panel/panelAccess/panelRoleAccess';

const Dashboard: React.FC = () => {
  const { currentUser } = useSelector((state: RootState) => state.user);
  const { t } = usePageTranslation('game', 'Dashboard');
  const { t: tHeader } = usePageTranslation('game', 'DashboardHeader');
  const { t: tStats } = usePageTranslation('game', 'StatsSection');
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [now, setNow] = useState(() => new Date());
  const [showSettings, setShowSettings] = useState(false);
  const [showSupport, setShowSupport] = useState(false);
  const [showRanking, setShowRanking] = useState(false);
  const [showShop, setShowShop] = useState(false);

  useUserProfileSync(5000, true);

  useKeyboardShortcuts({
    onSettingsToggle: () => setShowSettings(!showSettings),
    onMapToggle: () => navigate('/jeu'),
    enabled: !showSettings && !showSupport && !showRanking && !showShop,
  });

  useEffect(() => {
    const id = window.setInterval(() => setNow(new Date()), 1000);
    return () => window.clearInterval(id);
  }, []);

  const handleLogout = async () => {
    try {
      const sessionService = SessionService.getInstance();
      await sessionService.logout();
    } catch (error) {
      console.error('Erreur lors de la déconnexion:', error);
    } finally {
      dispatch(logout());
      localStorage.removeItem('token');
      navigate('/');
    }
  };

  const headerClock = now.toLocaleString('fr-FR', {
    weekday: 'short',
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });

  if (!currentUser) {
    return null;
  }

  const roleLabel = roleLabelFromStatsT(currentUser.roles_id, tStats);

  return (
    <div className="fixed inset-0 overflow-hidden bg-cockpit-bg/92 font-inter text-cockpit-text">
      <BackgroundImage />

      <header className="absolute inset-x-0 top-0 z-[1200] border-b border-cockpit-line bg-gradient-to-b from-[#16223a] to-[#0a111d] backdrop-blur-md shadow-[0_6px_24px_rgba(0,0,0,0.5)]">
        <div className="flex min-h-14 flex-wrap items-center justify-between gap-x-4 gap-y-2 px-4 py-2 sm:px-5">
          <div className="flex min-w-0 flex-1 flex-wrap items-center gap-3 sm:gap-4">
            <Link to="/" className="flex shrink-0 items-center gap-2.5">
              <img src="/images/logo.png" alt="Logo" className="h-9 w-9 rounded-md object-contain ring-1 ring-cockpit-line" />
              <div className="min-w-0 hidden sm:block">
                <p className="text-[9px] uppercase tracking-[0.22em] text-cockpit-muted">Tableau de bord</p>
                <p className="truncate text-sm font-bold tracking-wide text-cockpit-text">OPSECOURS WEB</p>
              </div>
            </Link>
            <div className="hidden items-center gap-2.5 border-l border-cockpit-line pl-4 text-[12px] sm:flex">
              <div className="rounded-md border border-cockpit-green/35 bg-cockpit-green/10 px-3 py-1.5 font-mono font-semibold text-cockpit-green">
                € {Number(currentUser.argents ?? 0).toLocaleString('fr-FR')}
              </div>
              <div className="rounded-md border border-cockpit-amber/35 bg-cockpit-amber/10 px-3 py-1.5 font-mono font-semibold text-cockpit-amber">
                🏅 {Number(currentUser.medailles ?? 0).toLocaleString('fr-FR')}
              </div>
            </div>
          </div>

          <div className="flex w-full max-w-full flex-none flex-wrap items-center justify-end gap-3 sm:w-auto sm:max-w-[75%] lg:max-w-[70%]">
            <div className="flex w-full max-w-full shrink-0 flex-wrap items-center justify-end gap-2 sm:w-auto sm:max-w-none sm:flex-nowrap sm:gap-2.5">
              <div
                className="flex min-w-0 max-w-full flex-1 justify-end sm:flex-initial sm:max-w-[min(100%,280px)] lg:max-w-[320px]"
                aria-label={t('header.clockHint')}
              >
                <time
                  dateTime={now.toISOString()}
                  className="w-full truncate rounded-md border border-cockpit-line bg-cockpit-panel/60 px-3 py-2 text-center font-mono text-[10px] font-medium leading-snug text-cockpit-text-2 sm:w-auto sm:text-[11px]"
                  title={t('header.clockHint')}
                >
                  {headerClock}
                </time>
              </div>

              {currentUser.roles_id != null && isPanelTeamMember(currentUser.roles_id) && (
                <nav className="flex shrink-0 items-center justify-end" aria-label={t('header.navPanel')}>
                  <button
                    type="button"
                    onClick={() => navigate('/panel')}
                    className="flex items-center gap-2 rounded-md border border-[#6366f1]/55 bg-[#6366f1]/18 px-3 py-2 text-sm font-medium text-[#c7d2fe] transition hover:bg-[#6366f1]/30"
                  >
                    <svg className="h-4 w-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z"
                      />
                    </svg>
                    {tHeader('panel')}
                  </button>
                </nav>
              )}

              <nav
                className="flex shrink-0 flex-wrap items-center justify-end gap-2"
                aria-label={t('header.navAssistance')}
              >
                <button
                  type="button"
                  onClick={() => setShowSupport(true)}
                  className="rounded-md border border-cockpit-line bg-cockpit-panel/60 px-3 py-2 text-sm font-medium text-cockpit-text-2 transition hover:border-cockpit-blue/50 hover:bg-cockpit-panel-2 hover:text-cockpit-text"
                >
                  {tHeader('support')}
                </button>
              </nav>
            </div>

            <div className="flex w-full flex-wrap items-center justify-end gap-2 sm:w-auto sm:gap-3">

              <span className="hidden h-8 w-px shrink-0 bg-cockpit-line sm:inline" aria-hidden />

              <nav
                className="flex flex-wrap items-center justify-end gap-2 sm:gap-2.5"
                aria-label={t('header.navGameExtras')}
              >
                <button
                  type="button"
                  onClick={() => setShowRanking(true)}
                  className="rounded-md border border-cockpit-amber/40 bg-cockpit-amber/10 px-3 py-2 text-sm font-medium text-cockpit-amber transition hover:bg-cockpit-amber/20"
                >
                  {tHeader('ranking')}
                </button>
                <button
                  type="button"
                  onClick={() => setShowShop(true)}
                  className="rounded-md border border-cockpit-green/40 bg-cockpit-green/10 px-3 py-2 text-sm font-medium text-cockpit-green transition hover:bg-cockpit-green/20"
                >
                  {tHeader('shop')}
                </button>
              </nav>

              {currentUser.roles_id === 1 && (
                <>
                  <span className="hidden h-8 w-px shrink-0 bg-cockpit-line sm:inline" aria-hidden />
                  <nav className="flex flex-wrap items-center justify-end" aria-label={t('header.navAllianceAdmin')}>
                    <button
                      type="button"
                      onClick={() => navigate('/dashboard/alliance')}
                      className="rounded-md border border-[#6366f1]/45 bg-[#6366f1]/15 px-3 py-2 text-sm font-medium text-[#c7d2fe] transition hover:bg-[#6366f1]/25"
                    >
                      {tHeader('alliance')}
                    </button>
                  </nav>
                </>
              )}

              <span className="hidden h-8 w-px shrink-0 bg-cockpit-line md:inline" aria-hidden />

              <nav className="flex flex-wrap items-center justify-end gap-2 sm:gap-2.5" aria-label={t('header.sessionNav')}>
                <button
                  type="button"
                  onClick={() => setShowSettings(true)}
                  className="rounded-md border border-cockpit-line bg-cockpit-panel/60 px-3 py-2 text-sm font-medium text-cockpit-text-2 transition hover:border-cockpit-blue/50 hover:bg-cockpit-panel-2 hover:text-cockpit-text"
                >
                  {tHeader('settings')}
                </button>
                <button
                  type="button"
                  onClick={handleLogout}
                  className="rounded-md border border-cockpit-red/40 bg-cockpit-red/10 px-3 py-2 text-sm font-medium text-cockpit-red transition hover:bg-cockpit-red/20"
                >
                  {tHeader('logout')}
                </button>
              </nav>

              <div className="ml-0 hidden max-w-[160px] truncate rounded-md border border-cockpit-green/35 bg-cockpit-green/10 px-3 py-2 text-center text-xs font-semibold uppercase tracking-wide text-cockpit-green sm:ml-1 sm:inline lg:max-w-[200px]">
                {currentUser.username}
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className="absolute inset-x-0 bottom-0 p-3 sm:p-4" style={{ top: '72px' }}>
        <div className="mx-auto grid h-full min-h-0 max-w-[1680px] grid-cols-1 gap-5 lg:grid-cols-[minmax(300px,400px),minmax(0,1fr)] xl:gap-8">
          <section className="relative flex min-h-[240px] flex-col overflow-hidden rounded-lg border border-cockpit-line/70 bg-cockpit-panel/70 shadow-2xl shadow-black/40 backdrop-blur-md lg:min-h-0">
            <div className="pointer-events-none absolute inset-0 rounded-lg bg-gradient-to-br from-cockpit-green/12 via-transparent to-cockpit-violet/12" />
            <div className="relative border-b border-cockpit-line/60 bg-cockpit-bg/55 px-4 py-4 sm:px-6">
              <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-cockpit-green">
                {t('welcomePanel.tagline')}
              </p>
              <p className="mt-1 text-[11px] font-medium text-cockpit-muted">{t('welcomePanel.taglineSub')}</p>
              <h2 className="mt-2 text-2xl font-bold tracking-tight text-cockpit-text sm:text-3xl">
                {t('welcome')},{' '}
                <span className="bg-gradient-to-r from-[#6ee7b7] to-[#5eead4] bg-clip-text text-transparent">
                  {currentUser.username}
                </span>
              </h2>
            </div>

            <div className="relative flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-4 sm:gap-5 sm:p-6">
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="rounded-md border border-cockpit-line/60 bg-cockpit-bg-2/60 p-3.5">
                  <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-cockpit-muted">{t('welcomePanel.idLabel')}</p>
                  <p className="mt-1.5 font-mono text-lg font-semibold text-cockpit-text">#{currentUser.id}</p>
                </div>
                <div className="rounded-md border border-cockpit-line/60 bg-cockpit-bg-2/60 p-3.5">
                  <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-cockpit-muted">{t('welcomePanel.roleLabel')}</p>
                  <p className="mt-1.5 text-lg font-semibold text-cockpit-violet">{roleLabel}</p>
                </div>
              </div>
              <div className="rounded-md border border-cockpit-line/60 bg-cockpit-bg-2/60 p-3.5">
                <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-cockpit-muted">{t('welcomePanel.emailLabel')}</p>
                <p className="mt-1.5 truncate text-sm font-medium text-cockpit-text-2">{currentUser.email}</p>
              </div>
              {(currentUser.department || currentUser.departement_code) && (
                <div className="rounded-md border border-cockpit-line/60 bg-cockpit-bg-2/60 p-3.5">
                  <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-cockpit-muted">{t('welcomePanel.departmentLabel')}</p>
                  <p className="mt-1.5 text-lg font-semibold text-cockpit-text">
                    {currentUser.department ?? String(currentUser.departement_code ?? '')}
                  </p>
                </div>
              )}

              <button
                type="button"
                onClick={() => navigate('/jeu')}
                title="Lancer le jeu"
                className="w-full rounded-md border border-cockpit-green/50 bg-cockpit-green/20 px-4 py-3.5 text-center shadow-lg shadow-emerald-900/25 transition hover:bg-cockpit-green/30 sm:py-4"
              >
                <span className="block text-sm font-bold uppercase tracking-[0.08em] text-cockpit-green">Jouer</span>
              </button>

              <div className="mt-auto flex flex-wrap gap-2 border-t border-cockpit-line/60 pt-4 sm:hidden">
                <div className="rounded-md border border-cockpit-green/40 bg-cockpit-green/10 px-2 py-1 font-mono text-[11px] font-semibold text-cockpit-green">
                  € {Number(currentUser.argents ?? 0).toLocaleString('fr-FR')}
                </div>
                <div className="rounded-md border border-cockpit-amber/40 bg-cockpit-amber/10 px-2 py-1 font-mono text-[11px] font-semibold text-cockpit-amber">
                  🏅 {Number(currentUser.medailles ?? 0).toLocaleString('fr-FR')}
                </div>
              </div>
            </div>
          </section>

          <section className="relative flex min-h-[320px] flex-col overflow-hidden rounded-lg border border-cockpit-line/70 bg-cockpit-panel/70 shadow-2xl shadow-black/40 backdrop-blur-md lg:min-h-0">
            <div className="pointer-events-none absolute inset-0 rounded-lg bg-gradient-to-br from-cockpit-blue/10 via-transparent to-cockpit-bg/85" />
            <div className="relative border-b border-cockpit-line/60 bg-cockpit-bg/55 px-4 py-4 sm:px-6">
              <h2 className="text-xl font-bold text-cockpit-text sm:text-2xl">{t('statsPanel.title')}</h2>
              <p className="mt-1 text-sm text-cockpit-text-2">{t('statsPanel.subtitle')}</p>
            </div>
            <div className="relative min-h-0 flex-1 overflow-y-auto px-3 py-4 sm:px-5 sm:py-5">
              <StatsSection currentUser={currentUser} />
            </div>
          </section>
        </div>
      </div>

      <SupportModal isOpen={showSupport} onClose={() => setShowSupport(false)} />

      <SettingsModal
        isOpen={showSettings}
        onClose={() => setShowSettings(false)}
        currentUser={currentUser}
      />

      <RankingModal isOpen={showRanking} onClose={() => setShowRanking(false)} />

      <ShopModal isOpen={showShop} onClose={() => setShowShop(false)} />
    </div>
  );
};

export default Dashboard;
